import { Action } from '@ngrx/store';

import type { OfferKind } from '@recruitee/offers-types';
import { OfferSimpleListItem } from '@recruitee/offers-ui';
import { SearchSegmentActions } from '@recruitee/search-data';
import type { SearchSegment } from '@recruitee/search-types';
import { buildResourceActions } from '@recruitee/utils';

import {
  CandidateDataTableColumnWithVisibility,
  FilterGroup,
  RecordsData,
  SegmentsCountsData,
  SortByType,
  ToggleSegmentDropdownEvent,
  ViewsCountsData,
  ViewType,
} from './search-page.types';

export type SearchAction<T extends Action> = { [P in keyof T]: T[P] };

export enum ActionType {
  INITIALIZE_VIEW = 'rt/search-feature/search-page/INITIALIZE_VIEW',
  DESTROY_VIEW = 'rt/search-feature/search-page/DESTROY_VIEW',

  LOAD_RECORDS = 'rt/search-feature/search-page/LOAD_RECORDS',
  FETCH_RECORDS = 'rt/search-feature/search-page/FETCH_RECORDS',
  FETCH_RECORDS_START = 'rt/search-feature/search-page/FETCH_RECORDS_START',
  FETCH_RECORDS_SUCCESS = 'rt/search-feature/search-page/FETCH_RECORDS_SUCCESS',
  FETCH_RECORDS_FAILURE = 'rt/search-feature/search-page/FETCH_RECORDS_FAILURE',
  SET_RECORDS_RELOADING = 'rt/search-feature/search-page/SET_RECORDS_RELOADING',

  MARK_CURRENT_SEGMENT = 'rt/search-feature/search-page/MARK_CURRENT_SEGMENT',
  SELECT_SEGMENT = 'rt/search-feature/search-page/SELECT_SEGMENT',
  TOGGLE_SEGMENT_DROPDOWN = 'rt/search-feature/search-page/TOGGLE_SEGMENT_DROPDOWN',
  TOGGLE_SEGMENTS_VISIBILITY = 'rt/search-feature/search-page/TOGGLE_SEGMENTS_VISIBILITY',

  FETCH_SEGMENTS_COUNTS = 'rt/search-feature/search-page/FETCH_SEGMENTS_COUNTS',
  FETCH_SEGMENTS_COUNTS_START = 'rt/search-feature/search-page/FETCH_SEGMENTS_COUNTS_START',
  FETCH_SEGMENTS_COUNTS_SUCCESS = 'rt/search-feature/search-page/FETCH_SEGMENTS_COUNTS_SUCCESS',
  FETCH_SEGMENTS_COUNTS_FAILURE = 'rt/search-feature/search-page/FETCH_SEGMENTS_COUNTS_FAILURE',
  FETCH_VIEWS_COUNTS = 'rt/search-feature/search-page/FETCH_VIEWS_COUNTS',
  FETCH_VIEWS_COUNTS_START = 'rt/search-feature/search-page/FETCH_VIEWS_COUNTS_START',
  FETCH_VIEWS_COUNTS_SUCCESS = 'rt/search-feature/search-page/FETCH_VIEWS_COUNTS_SUCCESS',
  FETCH_VIEWS_COUNTS_FAILURE = 'rt/search-feature/search-page/FETCH_VIEWS_COUNTS_FAILURE',
  SET_SHOWN_VIEWS_COUNTS = 'rt/search-feature/search-page/SET_SHOWN_VIEWS_COUNTS',

  SET_QUERY_PARAMS = 'rt/search-feature/search-page/SET_QUERY_PARAMS',
  SET_CAN_MANAGE_CANDIDATE = 'rt/search-feature/search-page/SET_CAN_MANAGE_CANDIDATE',
  CHANGE_OPTIONS_WITH_QUERY_PARAMS = 'rt/search-feature/search-page/CHANGE_OPTIONS_WITH_QUERY_PARAMS',
  GO_TO_VIEW = 'rt/search-feature/search-page/GO_TO_VIEW',
  CHANGE_VIEW = 'rt/search-feature/search-page/CHANGE_VIEW',
  CHANGE_QUERY = 'rt/search-feature/search-page/CHANGE_QUERY',
  CHANGE_FILTERS = 'rt/search-feature/search-page/CHANGE_FILTERS',
  RESET_FILTERS = 'rt/search-feature/search-page/RESET_FILTERS',
  CHANGE_PAGINATION = 'rt/search-feature/search-page/CHANGE_PAGINATION',
  CHANGE_SORT_BY = 'rt/search-feature/search-page/CHANGE_SORT_BY',
  CHANGE_TABLE_OPTIONS = 'rt/search-feature/search-page/CHANGE_TABLE_OPTIONS',

  OPEN_RECORDS_EXPORT = 'rt/search-feature/search-page/OPEN_RECORDS_EXPORT',
  CHANGE_GDPR_MODE = 'rt/search-feature/search-page/CHANGE_GDPR_MODE',
  SET_SAMPLE_DATA = 'rt/search-feature/search-page/SET_SAMPLE_DATA',
}

export type SearchActionUnion =
  | SearchAction<{
      type: ActionType.INITIALIZE_VIEW;
      queryParams: any;
      offer: OfferSimpleListItem | null;
    }>
  | SearchAction<{ type: ActionType.DESTROY_VIEW }>
  | SearchAction<{ type: ActionType.LOAD_RECORDS; data: RecordsData }>
  | SearchAction<{ type: ActionType.FETCH_RECORDS }>
  | SearchAction<{ type: ActionType.FETCH_RECORDS_START }>
  | SearchAction<{ type: ActionType.FETCH_RECORDS_SUCCESS; data: RecordsData }>
  | SearchAction<{ type: ActionType.FETCH_RECORDS_FAILURE; error: any }>
  | SearchAction<{ type: ActionType.SET_RECORDS_RELOADING; reloading: boolean }>
  | SearchAction<{ type: ActionType.MARK_CURRENT_SEGMENT; segment: SearchSegment | null }>
  | SearchAction<{ type: ActionType.SELECT_SEGMENT; segment: SearchSegment }>
  | SearchAction<{ type: ActionType.TOGGLE_SEGMENT_DROPDOWN; payload: ToggleSegmentDropdownEvent }>
  | SearchAction<{ type: ActionType.TOGGLE_SEGMENTS_VISIBILITY; allVisible: boolean }>
  | SearchAction<{ type: ActionType.FETCH_SEGMENTS_COUNTS }>
  | SearchAction<{ type: ActionType.FETCH_SEGMENTS_COUNTS_START }>
  | SearchAction<{ type: ActionType.FETCH_SEGMENTS_COUNTS_SUCCESS; data: SegmentsCountsData }>
  | SearchAction<{ type: ActionType.FETCH_SEGMENTS_COUNTS_FAILURE; error: any }>
  | SearchAction<{ type: ActionType.FETCH_VIEWS_COUNTS }>
  | SearchAction<{ type: ActionType.FETCH_VIEWS_COUNTS_START }>
  | SearchAction<{ type: ActionType.FETCH_VIEWS_COUNTS_SUCCESS; data: ViewsCountsData }>
  | SearchAction<{ type: ActionType.FETCH_VIEWS_COUNTS_FAILURE; error: any }>
  | SearchAction<{
      type: ActionType.SET_SHOWN_VIEWS_COUNTS;
      viewsCounts: { [key: string]: { count: number; stale: boolean | null } };
    }>
  | SearchAction<{ type: ActionType.SET_QUERY_PARAMS; queryParams: any }>
  | SearchAction<{ type: ActionType.SET_CAN_MANAGE_CANDIDATE; value: boolean }>
  | SearchAction<{
      type: ActionType.SET_SAMPLE_DATA;
      value: { hasSampleData: boolean; sampleDataInProgress: boolean };
    }>
  | SearchAction<{ type: ActionType.CHANGE_OPTIONS_WITH_QUERY_PARAMS; queryParams: any }>
  | SearchAction<{ type: ActionType.GO_TO_VIEW; view: ViewType }>
  | SearchAction<{ type: ActionType.CHANGE_VIEW; view: ViewType }>
  | SearchAction<{ type: ActionType.CHANGE_QUERY; query: string | null }>
  | SearchAction<{
      type: ActionType.CHANGE_FILTERS;
      payload: { filters: FilterGroup[]; preventReload?: boolean };
    }>
  | SearchAction<{ type: ActionType.RESET_FILTERS }>
  | SearchAction<{ type: ActionType.CHANGE_PAGINATION; offset: number; limit: number }>
  | SearchAction<{ type: ActionType.CHANGE_SORT_BY; sortBy: SortByType; setIsPristine?: boolean }>
  | SearchAction<{
      type: ActionType.CHANGE_TABLE_OPTIONS;
      autoFit?: boolean;
      columns?: CandidateDataTableColumnWithVisibility[];
    }>
  | SearchAction<{ type: ActionType.OPEN_RECORDS_EXPORT }>
  | ReturnType<(typeof SearchSegmentActions)['fetchCollectionSuccess']>;

export function initializeView({
  queryParams,
  offer,
}: {
  queryParams: any;
  offer: OfferSimpleListItem | null;
}): SearchActionUnion {
  return { type: ActionType.INITIALIZE_VIEW, queryParams, offer };
}

export function destroyView(): SearchActionUnion {
  return { type: ActionType.DESTROY_VIEW };
}

const { load: loadRecords, fetch: fetchRecords } = buildResourceActions<RecordsData>({
  LOAD: ActionType.LOAD_RECORDS,
  FETCH: ActionType.FETCH_RECORDS,
});
export { loadRecords, fetchRecords };

export function setRecordsReloading(reloading: boolean): SearchActionUnion {
  return { type: ActionType.SET_RECORDS_RELOADING, reloading };
}

export function markCurrentSegment(segment: SearchSegment | null): SearchActionUnion {
  return { type: ActionType.MARK_CURRENT_SEGMENT, segment };
}

export function selectSegment(segment: SearchSegment): SearchActionUnion {
  return { type: ActionType.SELECT_SEGMENT, segment };
}

export function toggleSegmentDropdown(payload: ToggleSegmentDropdownEvent): SearchActionUnion {
  return { type: ActionType.TOGGLE_SEGMENT_DROPDOWN, payload };
}

export function toggleSegmentsVisibility(allVisible: boolean): SearchActionUnion {
  return { type: ActionType.TOGGLE_SEGMENTS_VISIBILITY, allVisible };
}

const { fetch: fetchSegmentsCounts } = buildResourceActions<ViewsCountsData>({
  FETCH: ActionType.FETCH_SEGMENTS_COUNTS,
});
export { fetchSegmentsCounts };

const { fetch: fetchViewsCounts } = buildResourceActions<ViewsCountsData>({
  FETCH: ActionType.FETCH_VIEWS_COUNTS,
});
export { fetchViewsCounts };

export function setShownViewsCounts(viewsCounts: {
  [key: string]: { count: number; stale: boolean | null };
}): SearchActionUnion {
  return { type: ActionType.SET_SHOWN_VIEWS_COUNTS, viewsCounts };
}

export function setQueryParams(queryParams: any): SearchActionUnion {
  return { type: ActionType.SET_QUERY_PARAMS, queryParams };
}

export function setCanManageCandidate(value: boolean): SearchActionUnion {
  return { type: ActionType.SET_CAN_MANAGE_CANDIDATE, value };
}

export function setSampleData(value: {
  hasSampleData: boolean;
  sampleDataInProgress: boolean;
}): SearchActionUnion {
  return { type: ActionType.SET_SAMPLE_DATA, value };
}

export function changeOptionsWithUrlParams(queryParams: any): SearchActionUnion {
  return { type: ActionType.CHANGE_OPTIONS_WITH_QUERY_PARAMS, queryParams };
}

export function goToView(view: ViewType): SearchActionUnion {
  return { type: ActionType.GO_TO_VIEW, view };
}

export function changeView(view: ViewType): SearchActionUnion {
  return { type: ActionType.CHANGE_VIEW, view };
}

export function changeQuery(query: string | null): SearchActionUnion {
  return { type: ActionType.CHANGE_QUERY, query };
}

export function changeFilters(payload: {
  filters: FilterGroup[];
  preventReload?: boolean;
}): SearchActionUnion {
  return { type: ActionType.CHANGE_FILTERS, payload };
}

export function resetFilters(): SearchActionUnion {
  return { type: ActionType.RESET_FILTERS };
}

export function changePagination({
  offset,
  limit,
}: {
  offset: number;
  limit: number;
}): SearchActionUnion {
  return { type: ActionType.CHANGE_PAGINATION, offset, limit };
}

export function changeSortBy(sortBy: SortByType, setIsPristine?: boolean): SearchActionUnion {
  return { type: ActionType.CHANGE_SORT_BY, sortBy, setIsPristine };
}

export function changeTableOptions(options: {
  autoFit?: boolean;
  columns?: CandidateDataTableColumnWithVisibility[];
  offerKind?: OfferKind;
}): SearchActionUnion {
  return { type: ActionType.CHANGE_TABLE_OPTIONS, ...options };
}

export function openRecordsExport(): SearchActionUnion {
  return { type: ActionType.OPEN_RECORDS_EXPORT };
}
